import React from 'react';
import { FullRecipe } from '../../data/fullRecipes';

/**
 * The inside of a real recipe — the part that renders when a card actually
 * has weights and steps behind it rather than a "coming soon" note.
 *
 * Lives apart from RecipeModal so the modal stays a shell (open, close,
 * escape, scroll-lock) and this stays typography. Also means a full recipe
 * can later be rendered on its own page without touching the modal.
 *
 * Weights only, by design (Jarvis, 2026-09-12) — see fullRecipes.ts.
 */
export function FullRecipeBody({ recipe }: { recipe: FullRecipe }) {
  return (
    <div className="full-recipe">
      <div className="full-recipe-yield">{recipe.yieldLine}</div>

      {recipe.intro.map((p, i) => (
        <p key={i} className="full-recipe-intro">{p}</p>
      ))}

      {recipe.contains && (
        <p className="full-recipe-contains">
          <strong>Allergens.</strong> {recipe.contains}
        </p>
      )}

      <div className="section-label full-recipe-head">Ingredients</div>
      {recipe.groups.map((g) => (
        <div key={g.label} className="full-recipe-group">
          <div className="full-recipe-group-label">{g.label}</div>
          <ul className="full-recipe-ing">
            {g.items.map((it) => (
              <li key={it.item}>
                <span className="full-recipe-amt">{it.amount}</span>
                <span className="full-recipe-item">
                  {it.item}
                  {it.note && <em className="full-recipe-note"> — {it.note}</em>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}

      <div className="section-label full-recipe-head">Method</div>
      <ol className="full-recipe-steps">
        {recipe.steps.map((s) => (
          <li key={s.heading}>
            <h4>{s.heading}</h4>
            {s.body.map((b, i) => (
              <p key={i}>{b}</p>
            ))}
          </li>
        ))}
      </ol>

      {recipe.stats && (
        <div className="full-recipe-stats">
          <div className="full-recipe-group-label">{recipe.stats.label}</div>
          <p className="full-recipe-stats-caption">{recipe.stats.caption}</p>
          <table className="full-recipe-table">
            <thead>
              <tr>
                <th scope="col">Per brownie</th>
                <th scope="col">Without</th>
                <th scope="col">With TrueNooch™</th>
                <th scope="col">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {recipe.stats.rows.map((r) => (
                <tr key={r.label}>
                  <th scope="row">{r.label}</th>
                  <td>{r.base}</td>
                  <td className="full-recipe-td-strong">{r.withFlour}</td>
                  <td className="full-recipe-td-delta">{r.delta}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="full-recipe-footnote">{recipe.stats.footnote}</p>
        </div>
      )}

      <div className="section-label full-recipe-head">Keeping them</div>
      <p className="full-recipe-intro">{recipe.storage}</p>
    </div>
  );
}
